import React from 'react';
import { Database, Layers, Tag, Gauge, Network, Activity } from 'lucide-react';
import GraphCanvasShell from './GraphCanvasShell';

const isImpacted = (status) => String(status || '').toLowerCase() === 'impacted';

const fullTitle = (value) => {
    if (value == null) return undefined;
    const text = String(value).trim();
    if (!text || text === '—') return undefined;
    return text;
};

const formatSyncTime = (value) => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (!Number.isNaN(date.valueOf())) {
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            });
        }
    } catch (e) {
        // fall through
    }
    return String(value);
};

const formatMetricLabel = (metric) => {
    if (metric == null) return '—';
    if (typeof metric === 'string' || typeof metric === 'number') return String(metric);

    const name = metric.metrics_name || metric.name || 'Metric';
    const value = metric.metrics_value != null && metric.metrics_value !== ''
        ? String(metric.metrics_value)
        : null;
    const units = metric.units != null && metric.units !== '' ? String(metric.units) : '';
    const valueText = value != null ? `${value}${units}` : null;

    return valueText ? `${name} ${valueText}` : name;
};

const metricDirectionClass = (metric) => {
    const direction = String(metric?.direction || '').toLowerCase();
    if (direction === 'up' || direction === 'increase') return 'up';
    if (direction === 'down' || direction === 'decrease') return 'dn';
    return '';
};

const renderPipelineMetrics = (pipeline) => {
    const metrics = Array.isArray(pipeline?.metrics) ? pipeline.metrics : [];
    if (!metrics.length) return null;

    return (
        <div className="c-sec">
            <div className="c-sec-lbl">Metrics</div>
            {metrics.map((metric, idx) => {
                const label = formatMetricLabel(metric);
                const value = metric?.metrics_value != null && metric?.metrics_value !== ''
                    ? `${metric.metrics_value}${metric.units || ''}`
                    : null;
                const dirClass = metricDirectionClass(metric);
                const impacted = isImpacted(metric?.status);

                return (
                    <div
                        key={`${pipeline.pipeline_id || pipeline.pipeline_name}-m-${idx}`}
                        className={`c-item${impacted ? ' red' : ''}`}
                        title={fullTitle(label)}
                    >
                        <span className="fi"><Activity size={13} /></span>
                        {metric?.metrics_name || metric?.name || 'Metric'}
                        {value != null && (
                            <>
                                {' '}
                                <span className={`met${dirClass ? ` ${dirClass}` : ''}`}>{value}</span>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default class LineageModal extends React.Component {
    render() {
        const { show, onClose, row = {}, data = null, loading = false, error = null } = this.props;
        if (!show) return null;

        const kpi = data?.kpi || {};
        const datasource = data?.datasource || {};
        const pipelines = Array.isArray(data?.pipelines) ? data.pipelines : [];
        const stats = data?.stats || {};
        const datasourceDatasets = Array.isArray(datasource.datasets) ? datasource.datasets : [];

        const uniqueDatasetCount = (() => {
            const keys = new Set();
            datasourceDatasets.forEach((ds) => {
                const key = ds?.data_set_id ?? ds?.data_set_name;
                if (key != null && key !== '') keys.add(String(key));
            });
            pipelines.forEach((pipeline) => {
                (Array.isArray(pipeline?.datasets) ? pipeline.datasets : []).forEach((ds) => {
                    const key = ds?.data_set_id ?? ds?.data_set_name;
                    if (key != null && key !== '') keys.add(String(key));
                });
            });
            return keys.size;
        })();

        const mappedAttributes = kpi.mapped_attributes;
        const mappedAttributeCount = Array.isArray(mappedAttributes)
            ? mappedAttributes.length
            : (mappedAttributes?.name ? 1 : 0);

        const datasourceCount = stats.datasource_count ?? datasourceDatasets.length;
        const pipelineCount = stats.pipeline_count ?? pipelines.length;
        const datasetCount = stats.dataset_count ?? uniqueDatasetCount;
        const attributeCount = stats.attribute_count ?? mappedAttributeCount;
        const kpiCount = stats.kpi_count ?? (kpi.kpi_id || kpi.kpi_name ? 1 : 0);

        const kpiName = kpi.kpi_name || row.kpi_name || row.kpiName || 'KPI';
        const formula = kpi.formula || row.formula || '—';
        const attributeName = (Array.isArray(mappedAttributes) ? mappedAttributes[0]?.name : mappedAttributes?.name)
            || row.attributeName
            || row.attribute
            || '—';
        const attributeImpacted = Array.isArray(mappedAttributes)
            ? mappedAttributes.some((attr) => isImpacted(attr?.status))
            : isImpacted(mappedAttributes?.status);
        const driftValue = kpi.drift_value != null && kpi.drift_value !== ''
            ? `${kpi.drift_value}${kpi.drift_unit || '%'}`
            : null;
        const datasourceName = datasource.datasource_name
            || row.datasourceName
            || row.datasource
            || 'Datasource';
        const datasourceType = datasource.type || 'Source';

        const primaryPipeline = pipelines[0] || null;
        const secondaryPipeline = pipelines[1] || null;

        return (
            <GraphCanvasShell
                show={show}
                onClose={onClose}
                defaultTheme="dark"
                contentKey={`${loading ? 'loading' : 'ready'}-${kpi.kpi_id || kpiName}-${pipelines.length}-${datasourceDatasets.length}`}
                title={(
                    <>
                        <span className="k"><Gauge size={18} /> KPI:</span>{' '}
                        <span title={fullTitle(kpiName)}>{kpiName}</span>
                    </>
                )}
                stats={(
                    <div className="lin-stats">
                        <span className="lin-stat">
                            <span className="ic"><Database size={15} /></span>{' '}
                            Datasource: <b>{datasourceCount}</b>
                        </span>
                        <span className="lin-stat">
                            <span className="ic"><Network size={15} /></span>{' '}
                            Pipelines: <b>{pipelineCount}</b>
                        </span>
                        <span className="lin-stat">
                            <span className="ic"><Layers size={15} /></span>{' '}
                            Datasets: <b>{datasetCount}</b>
                        </span>
                        <span className="lin-stat">
                            <span className="ic"><Tag size={15} /></span>{' '}
                            Attributes: <b>{attributeCount}</b>
                        </span>
                        <span className="lin-stat">
                            <span className="ic"><Gauge size={15} /></span>{' '}
                            KPIs: <b>{kpiCount}</b>
                        </span>
                    </div>
                )}
                headerExtra={(
                    <div className="lin-sync">
                         Identified Time  <b>{formatSyncTime(data?.last_sync_time)}</b>
                    </div>
                )}
                legend={(
                    <div className="lin-legend">
                        <span className="lg"><span className="sw sw-r"></span> Drift / impacted</span>
                        <span className="lg"><span className="sw sw-n"></span> Unaffected</span>
                    </div>
                )}
            >
                {loading && (
                    <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-text-white tw-text-[14px]">
                        Loading diagnostics map...
                    </div>
                )}
                {!loading && error && (
                    <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-text-[#F7BAC1] tw-text-[14px] tw-px-6 tw-text-center">
                        {error}
                    </div>
                )}
                {!loading && !error && data && (
                    <>
                        {(() => {
                            const hasPipelines = Boolean(primaryPipeline);
                            const hasTwoPipelines = Boolean(secondaryPipeline);
                            // Layout: with pipelines keep mid column; without pipelines pull mapped card left
                            const mappedLeft = hasPipelines ? 730 : 330;
                            const kpiLeft = hasPipelines ? 1090 : 690;
                            // Sparse graphs (0–1 pipeline) sit high in a 780px canvas — shift down to center
                            const yOff = hasTwoPipelines ? 0 : 90;
                            const mappedTop = 260 + yOff;
                            const dsTop = 315 + yOff;
                            const kpiTop = (hasPipelines ? 305 : 300) + yOff;
                            const pipeTop = (secondaryPipeline ? 55 : 260) + yOff;
                            const y = (v) => v + yOff;

                            const edgeToMappedImpacted = hasPipelines
                                ? (isImpacted(primaryPipeline?.status) || attributeImpacted)
                                : attributeImpacted;

                            return (
                                <>
                                    <svg
                                        className="lin-edges"
                                        viewBox="0 0 1320 780"
                                        preserveAspectRatio="none"
                                        style={{ width: '1320px', height: '780px' }}
                                    >
                                        {hasTwoPipelines ? (
                                            <>
                                                <path className={isImpacted(primaryPipeline?.status) ? 'ed-red' : 'ed-grey'} d="M250 360 C300 360,285 150,330 150" />
                                                <path className={isImpacted(secondaryPipeline?.status) ? 'ed-red' : 'ed-grey'} d="M250 360 C300 360,285 555,330 555" />
                                                <path className={isImpacted(primaryPipeline?.status) || attributeImpacted ? 'ed-red' : 'ed-grey'} d="M580 150 C665 150,650 330,730 330" />
                                                <path className={isImpacted(secondaryPipeline?.status) ? 'ed-red' : 'ed-grey'} d="M580 555 C665 555,650 330,730 330" />
                                                <path className={attributeImpacted || driftValue ? 'ed-red' : 'ed-grey'} d="M980 330 C1035 330,1040 355,1090 355" />
                                            </>
                                        ) : hasPipelines ? (
                                            <>
                                                <path className={isImpacted(primaryPipeline?.status) ? 'ed-red' : 'ed-grey'} d={`M250 ${y(360)} C300 ${y(360)},285 ${y(360)},330 ${y(360)}`} />
                                                <path className={edgeToMappedImpacted ? 'ed-red' : 'ed-grey'} d={`M580 ${y(360)} C665 ${y(360)},650 ${y(330)},730 ${y(330)}`} />
                                                <path className={attributeImpacted || driftValue ? 'ed-red' : 'ed-grey'} d={`M980 ${y(330)} C1035 ${y(330)},1040 ${y(355)},1090 ${y(355)}`} />
                                            </>
                                        ) : (
                                            <>
                                                {/* No pipeline tier — connect datasource → mapped attributes → KPI */}
                                                <path className={attributeImpacted ? 'ed-red' : 'ed-grey'} d={`M250 ${y(360)} C285 ${y(360)},300 ${y(330)},330 ${y(330)}`} />
                                                <path className={attributeImpacted || driftValue ? 'ed-red' : 'ed-grey'} d={`M580 ${y(330)} C630 ${y(330)},650 ${y(355)},690 ${y(355)}`} />
                                            </>
                                        )}
                                    </svg>

                                    <div className="card" style={{ left: 35, top: dsTop, width: 215, position: 'absolute' }}>
                                        <div className="c-head">
                                            <span className="c-ico"><Database size={13} /></span>
                                            <span className="c-name" title={fullTitle(datasourceName)}>{datasourceName}</span>
                                            <span className="c-type">{datasourceType}</span>
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Datasets</div>
                                            {datasourceDatasets.length > 0 ? datasourceDatasets.map((ds) => (
                                                <div
                                                    key={`${ds.data_set_id || ds.data_set_name}`}
                                                    className={`c-item${isImpacted(ds.status) ? ' red' : ''}`}
                                                    title={fullTitle(ds.data_set_name || '—')}
                                                >
                                                    <span className="fi"><Layers size={13} /></span>{ds.data_set_name || '—'}
                                                </div>
                                            )) : (
                                                <div className="c-item"><span className="fi"><Layers size={13} /></span>—</div>
                                            )}
                                        </div>
                                    </div>

                                    {primaryPipeline && (
                                        <div
                                            className={`card${isImpacted(primaryPipeline.status) ? ' crit' : ''}`}
                                            style={{ left: 330, top: pipeTop, width: 250, position: 'absolute' }}
                                        >
                                            <div className="c-head">
                                                <span className="c-ico"><Network size={13} /></span>
                                                <span className="c-name" title={fullTitle(primaryPipeline.pipeline_name)}>{primaryPipeline.pipeline_name}</span>
                                                {isImpacted(primaryPipeline.status) ? (
                                                    <span className="c-badge"><span className="d"></span>DRIFT</span>
                                                ) : (
                                                    <span className="c-type">Pipeline</span>
                                                )}
                                            </div>
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Datasets</div>
                                                {(Array.isArray(primaryPipeline.datasets) ? primaryPipeline.datasets : []).map((ds) => (
                                                    <div
                                                        key={`${primaryPipeline.pipeline_id}-${ds.data_set_name}`}
                                                        className={`c-item${isImpacted(ds.status) ? ' red' : ''}`}
                                                        title={fullTitle(ds.data_set_name || '—')}
                                                    >
                                                        <span className="fi"><Layers size={13} /></span>{ds.data_set_name || '—'}
                                                    </div>
                                                ))}
                                            </div>
                                            {renderPipelineMetrics(primaryPipeline)}
                                        </div>
                                    )}

                                    {secondaryPipeline && (
                                        <div
                                            className={`card${isImpacted(secondaryPipeline.status) ? ' crit' : ''}`}
                                            style={{ left: 330, top: 445, width: 250, position: 'absolute' }}
                                        >
                                            <div className="c-head">
                                                <span className="c-ico"><Network size={13} /></span>
                                                <span className="c-name" title={fullTitle(secondaryPipeline.pipeline_name)}>{secondaryPipeline.pipeline_name}</span>
                                                {isImpacted(secondaryPipeline.status) ? (
                                                    <span className="c-badge"><span className="d"></span>DRIFT</span>
                                                ) : (
                                                    <span className="c-type">Pipeline</span>
                                                )}
                                            </div>
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Datasets</div>
                                                {(Array.isArray(secondaryPipeline.datasets) ? secondaryPipeline.datasets : []).map((ds) => (
                                                    <div
                                                        key={`${secondaryPipeline.pipeline_id}-${ds.data_set_name}`}
                                                        className={`c-item${isImpacted(ds.status) ? ' red' : ''}`}
                                                        title={fullTitle(ds.data_set_name || '—')}
                                                    >
                                                        <span className="fi"><Layers size={13} /></span>{ds.data_set_name || '—'}
                                                    </div>
                                                ))}
                                            </div>
                                            {renderPipelineMetrics(secondaryPipeline)}
                                        </div>
                                    )}

                                    <div
                                        className={`card${attributeImpacted ? ' crit' : ''}`}
                                        style={{ left: mappedLeft, top: mappedTop, width: 250, position: 'absolute' }}
                                    >
                                        <div className="c-head">
                                            <span className="c-ico"><Tag size={13} /></span>
                                            <span
                                                className="c-name"
                                                title={fullTitle(
                                                    hasPipelines
                                                        ? (primaryPipeline?.pipeline_name || 'Mapped attributes')
                                                        : 'Mapped attributes'
                                                )}
                                            >
                                                {hasPipelines
                                                    ? (primaryPipeline?.pipeline_name || 'Mapped attributes')
                                                    : 'Mapped attributes'}
                                            </span>
                                            {attributeImpacted ? (
                                                <span className="c-badge"><span className="d"></span>IMPACTED</span>
                                            ) : (
                                                <span className="c-type">Mapped</span>
                                            )}
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Attributes</div>
                                            {Array.isArray(mappedAttributes) && mappedAttributes.length > 0 ? (
                                                mappedAttributes.map((attr) => (
                                                    <div
                                                        key={attr.name || attr}
                                                        className={`c-item${isImpacted(attr?.status) ? ' red' : ''}`}
                                                        title={fullTitle(attr.name || attr)}
                                                    >
                                                        <span className="fi"><Tag size={13} /></span>{attr.name || attr}
                                                    </div>
                                                ))
                                            ) : (
                                                <div
                                                    className={`c-item${attributeImpacted ? ' red' : ''}`}
                                                    title={fullTitle(attributeName)}
                                                >
                                                    <span className="fi"><Tag size={13} /></span>{attributeName}
                                                </div>
                                            )}
                                        </div>
                                        {driftValue && (
                                            <div className="c-sec">
                                                <div className="c-sec-lbl">Metrics</div>
                                                <div className="c-item red" title={fullTitle(`Drift ${driftValue}`)}>
                                                    <span className="fi"><Activity size={13} /></span>
                                                    Drift <span className="met dn">{driftValue}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="card crit focus kpi" style={{ left: kpiLeft, top: kpiTop, width: 230, position: 'absolute' }}>
                                        <div className="c-head">
                                            <span className="c-ico"><Gauge size={13} /></span>
                                            <span className="c-name" title={fullTitle(kpiName)}>{kpiName}</span>
                                            {driftValue ? (
                                                <span className="c-badge"><span className="d"></span>{driftValue}</span>
                                            ) : null}
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Main KPI · mapped attribute</div>
                                            <div
                                                className={`c-item${attributeImpacted ? ' red' : ''}`}
                                                title={fullTitle(attributeName)}
                                            >
                                                <span className="fi"><Tag size={13} /></span>{attributeName}
                                            </div>
                                        </div>
                                        <div className="c-sec">
                                            <div className="c-sec-lbl">Formula</div>
                                            <div className="c-formula" title={fullTitle(formula)}>{formula}</div>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}
            </GraphCanvasShell>
        );
    }
}
